import React, { CSSProperties, RefObject } from 'react';
import { UseQueryObjectProps, UseQuerySqlProps } from '@fabric-ui/components';
import { SxProps, Theme } from '@mui/material';
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnSizingState,
  InitialTableState,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableOptions,
} from '@tanstack/react-table';
import { RequestDocument, Variables } from 'graphql-request';

export enum TableSizeEnum {
  xSmall = 'xSmall',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Tiles = 'tiles',
}

export enum FormatterType {
  boolean = 'bool',
  number = 'number',
  percentage = 'percentage',
  historicDate = 'historic-date',
  string = 'string',
  date = 'date',
  array = 'array',
  json = 'json',
  loading = 'loading',
}

export enum OptionQueryType {
  GQL = 'gql',
  SQL = 'sql',
  Object = 'queryObject',
  GQL_PAGINATION = 'gqlPagination',
}

type GqlAsyncOptions = {
  type: OptionQueryType;
  query: RequestDocument;
  variables?: Variables;
  select: (data: any) => { title: string; value: any }[];
};

type GqlPaginationAsyncOptions = {
  type: OptionQueryType;
  query: RequestDocument;
  variables?: Variables;
  select: (data: any) => { data: { title: string; value: any }[]; totals: number };
};

type SqlAsyncOptions = {
  type: OptionQueryType;
} & UseQuerySqlProps;

type QueryObjectAsyncOptions = {
  type: OptionQueryType;
} & UseQueryObjectProps;

export type AsyncGetOptions = (search: string) => GqlAsyncOptions | SqlAsyncOptions | QueryObjectAsyncOptions | GqlPaginationAsyncOptions;

export type DataRecord = Record<string, any>;

export type TActiveRowId = string | undefined;

export interface FilterConfig {
  options?: any[];
  asyncGetOptions?: AsyncGetOptions;
  getOptions?: (data: any, label: React.ReactNode, v) => any[];
  getFilterOptionFunc?: (row: any) => { value: any; title: string };
  type?: FormatterType;
}

export interface TColumnDefMeta {
  isKey: boolean;
  hidden: boolean;
  filter: FilterConfig;
  type: FormatterType;
  style: CSSProperties;
  rowCellStyle: CSSProperties | (({ theme, row }: { theme: Theme; row: any }) => CSSProperties);
  isColumnHighlighted: boolean;
  isCustomCell: boolean;
}

export type RequiredIdColumnDef<TData extends DataRecord> = Omit<ColumnDef<TData>, 'meta'> & {
  id: string;
  meta?: Partial<TColumnDefMeta>;
};

export type TColumnsDef = RequiredIdColumnDef<DataRecord>[];

// make this deprecated when AvTableV2 effort is completed.
// /**
//  * @deprecated - Use {@link SortingState} instead
//  */
export type TOldSortingState = { property: string; isAsc?: boolean }[];

export enum TableV2PaginationType {
  CLIENT_CONTROLLED = 'CLIENT_CONTROLLED',
  CLIENT_NOT_CONTROLLED = 'CLIENT_NOT_CONTROLLED',
  SERVER_CONTROLLED = 'SERVER_CONTROLLED',
}

export type ClientSideControlledPagination = {
  type: TableV2PaginationType.CLIENT_CONTROLLED;
  state: PaginationState;
  onPaginationChange: (newPagination: PaginationState) => void;
};

export type ClientSideNotControlledPagination = {
  type: TableV2PaginationType.CLIENT_NOT_CONTROLLED;
  rowsPerPage?: number;
};

export type ServerSideControlledPagination = {
  type: TableV2PaginationType.SERVER_CONTROLLED;
  state: PaginationState;
  onPaginationChange: (newPagination: PaginationState) => void;
  totalRows: number;
};

export type TableV2Pagination = ClientSideControlledPagination | ClientSideNotControlledPagination | ServerSideControlledPagination;

export interface IAvTableV2Base {
  rows?: DataRecord[];
  headCells: TColumnsDef;
  loading?: boolean;
  storageKey?: string;
  multiSelect?: boolean;
  size?: TableSizeEnum;
  activeRowId?: TActiveRowId;
  onActiveRowIdChange?: (newActiveRowId: TActiveRowId) => void;
  initialState?: Pick<InitialTableState, 'columnFilters' | 'pagination' | 'sorting'>;
  sorting?: SortingState;
  onSortingChange?: (newSorting: SortingState) => void;
  pagination?: TableV2Pagination;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (rowSelection: RowSelectionState, rows: DataRecord[]) => void;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (newFilters: ColumnFiltersState) => void;
  enableActiveRowHighlighting?: boolean;
  sx?: SxProps<Theme>;
  onScroll?: (event: React.UIEvent<HTMLDivElement>, tableContainerRef: RefObject<HTMLDivElement | null>) => void;
  onIsAllRowsSelectedChange?: (isAllRowsSelected: boolean) => void;
  disableRowClickNavigation?: (selectedRow: DataRecord) => boolean;
  inlineActions?: ((selectedRow: DataRecord) => React.ReactNode)[];
  hasHeaderFilters?: boolean;
  allowKeyboardNavigation?: boolean;
  resizable?: boolean;
  shouldDisplayNull?: boolean;
  tanstackTableProps?: Partial<TableOptions<DataRecord>>;
  onNavigate: (route: string | any) => void;
  localColumnsWidth: number;
  onColumnResizeEnd: (key: string, value: ColumnSizingState) => void;
  FieldFilter: any;
}
